import { Button, Modal, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { createSpaceMutation, errorMessage, listPinsQueryKey, listSpacesQueryKey, schemas } from "@/api";
import { useAppForm } from "@/components/form/form";

const create = schemas.zSpaceCreate.shape;

/**
 * Placeholder "New space" form (name + description) until there's a design.
 * On success the new space opens. Mount when open.
 */
export function NewSpaceModal({ onClose }: Readonly<{ onClose: () => void }>) {
  const { t } = useTranslation("spaces");
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createSpace = useMutation(createSpaceMutation());
  const schema = z.object({
    name: z.string().trim().min(1, t("create.nameRequired")).pipe(create.name),
    description: z.string().max(5000),
  });

  const form = useAppForm({
    defaultValues: { name: "", description: "" },
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      try {
        const { data } = await createSpace.mutateAsync({
          body: { name: value.name.trim(), description: value.description.trim() || null },
        });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: listSpacesQueryKey() }),
          queryClient.invalidateQueries({ queryKey: listPinsQueryKey() }),
        ]);
        onClose();
        navigate(`/spaces/${data.uuid}`);
      } catch (error) {
        toast.error(errorMessage(error, t("create.failed")));
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t("create.title")}
      actions={
        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(submitting) => (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t("create.cancel")}
              </Button>
              <Button variant="primary" loading={submitting} onClick={() => form.handleSubmit()}>
                {t("create.create")}
              </Button>
            </>
          )}
        </form.Subscribe>
      }
    >
      <form
        aria-label={t("create.label")}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppField name="name">
          {(f) => <f.TextField label={t("create.name")} required placeholder={t("create.namePlaceholder")} />}
        </form.AppField>
        <form.AppField name="description">
          {(f) => <f.TextareaField label={t("create.description")} placeholder={t("create.descriptionPlaceholder")} />}
        </form.AppField>
      </form>
    </Modal>
  );
}
